import {isFeatureEnabled} from '@github-ui/feature-flags'
import {getBaseFetchHeaders} from '@github-ui/fetch-headers'
import type {FileType} from '@github-ui/markdown-editor'
import {isEnterprise} from '@github-ui/runtime-environment'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

function reportAttachmentUploadError(cause: unknown, message: string) {
  setTimeout(() => {
    const error = new Error(message, {cause})
    error.name = 'AttachmentUploadError'
    throw error
  })
}

function getUploadCredentials(): RequestCredentials {
  return isEnterprise() ? 'include' : 'same-origin'
}

// 204 No Content
type S3ResponseData = Record<string, unknown>

// 201 Created
type EnterpriseResponseData = {
  id: number
  href: string
}

type UploadResponseData = EnterpriseResponseData | S3ResponseData

type S3UploadPolicy = {
  asset_upload_url: string
  asset_upload_authenticity_token: string
  upload_authenticity_token: string
  upload_url: string
  form: Record<string, string>
  header: Record<string, string>
  same_origin: boolean
  asset: {
    id: number
    href: string
    name: string
  }
}

type EnterpriseUploadPolicy = {
  upload_authenticity_token: string
  upload_url: string
  form: {[key: string]: string}
  header: Record<string, string>
  same_origin: boolean
  asset: {
    name: string
  }
}

type UploadPolicyData = S3UploadPolicy | EnterpriseUploadPolicy

type UploadWithProgressOptions = {
  url: string
  formData: FormData
  headers: Record<string, string>
  onProgress: (uploadedBytes: number) => void
}

const parseResponseHeaders = (rawHeaders: string): Headers => {
  const headers = new Headers()
  for (const line of rawHeaders.trim().split(/\r?\n/)) {
    if (!line) continue
    const separatorIndex = line.indexOf(':')
    if (separatorIndex === -1) continue
    const name = line.slice(0, separatorIndex).trim()
    const value = line.slice(separatorIndex + 1).trim()
    if (name) headers.append(name, value)
  }
  return headers
}

const postWithProgress = ({url, formData, headers, onProgress}: UploadWithProgressOptions) =>
  new Promise<Response>((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.addEventListener('loadstart', () => onProgress(0))

    xhr.upload.addEventListener('progress', event => onProgress(event.loaded))

    // To mimic `fetch` API, both success and error responses from server will resolve the promise
    xhr.addEventListener('loadend', () => {
      if (xhr.status === 0) {
        // Network error or abort
        reject(new TypeError('Network request failed'))
        return
      }

      try {
        resolve(
          new Response(xhr.response || undefined, {
            status: xhr.status,
            statusText: xhr.statusText,
            headers: parseResponseHeaders(xhr.getAllResponseHeaders() ?? ''),
          }),
        )
      } catch (error) {
        reject(error)
      }
    })

    xhr.open('POST', url)
    if (isEnterprise()) xhr.withCredentials = true

    xhr.responseType = 'text'

    for (const [key, value] of Object.entries(headers)) {
      if (value !== undefined) xhr.setRequestHeader(key, value)
    }

    xhr.send(formData)
  })

/**
 * File types that are acceptable in any context.
 * Source: `Storage::Uplodable::CONTENT_TYPES`
 */
export const REPOLESS_ACCEPTED_FILE_TYPES: FileType[] = [
  '.svg',
  '.gif',
  '.jpg',
  '.jpeg',
  '.png',
  '.mov',
  '.mp4',
  '.webm',
]

/**
 * Baseline file types that are always acceptable when there is a target repo to upload to.
 * Source: `ApplicationRecord::Domain::AssetObjects::RepositoryFile::baseline_content_types`
 */
export const REPO_BASELINE_FILE_TYPES: FileType[] = [
  '.pdf',
  '.docx',
  '.odt',
  '.fodt',
  '.pptx',
  '.odp',
  '.fodp',
  '.xlsx',
  '.ods',
  '.fods',
  '.xls',
  '.csv',
  '.odg',
  '.fodg',
  '.odf',
  '.zip',
  '.gz',
  '.tgz',
  '.patch',
  '.txt',
  '.log',
  '.md',
  '.json',
  '.jsonc',
  '.cpuprofile',
  '.dmp',
]

/**
 * Expanded file types that are only acceptable when the feature flag is enabled.
 * Source: `ApplicationRecord::Domain::AssetObjects::RepositoryFile::expanded_content_types`
 */
export const REPO_EXPANDED_FILE_TYPES: FileType[] = [
  '.debug',
  '.rtf',
  '.msg',
  '.doc',
  '.eml',
  '.xlsm',
  '.tsv',
  '.py',
  '.yaml',
  '.yml',
  '.css',
  '.xml',
  '.html',
  '.htm',
  '.js',
  '.sql',
  '.java',
  '.c',
  '.cpp',
  '.ipynb',
  '.sh',
  '.php',
  '.ts',
  '.tsx',
  '.cs',
  '.pdb',
  '.drawio',
  '.copilotmd',
  // # Note: Currently disabled until we ensure they can be scanned: https://github.com/github/security-reviews/issues/1835
  //'.heic',
  //'.jfif',
  '.bmp',
  '.tif',
  '.tiff',
  '.mp3',
  '.wav',
]

export const REPO_WEBP_SUPPORT: FileType[] = ['.webp']

async function getPolicy(file: File, repositoryId?: string, projectId?: string) {
  const formData = new FormData()
  const {name, size, type} = file
  if (repositoryId) formData.append('repository_id', repositoryId)
  if (projectId) {
    formData.append('upload_container_type', 'project')
    formData.append('upload_container_id', projectId)
  }
  formData.append('name', name)
  formData.append('size', size.toString())
  formData.append('content_type', type)

  const response = await verifiedFetch('/upload/policies/assets', {body: formData, method: 'POST'})

  return response
}

async function uploadFileToStorage(
  policy: UploadPolicyData,
  file: File,
  onProgress?: (uploadedBytes: number) => void,
): Promise<Response> {
  const {upload_authenticity_token, upload_url, header, form, same_origin} = policy
  const uploadFormData = new FormData()

  if (same_origin) {
    // eslint-disable-next-line github/authenticity-token
    uploadFormData.append('authenticity_token', upload_authenticity_token)
  }

  for (const key in form) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    uploadFormData.append(key, form[key]!)
  }
  uploadFormData.append('file', file)

  let uploadResponse: Response
  try {
    uploadResponse = onProgress
      ? await postWithProgress({
          url: upload_url,
          formData: uploadFormData,
          headers: header,
          onProgress,
        })
      : await fetch(upload_url, {
          headers: {...header},
          method: 'post',
          body: uploadFormData,
          credentials: getUploadCredentials(),
        })
  } catch (error) {
    reportAttachmentUploadError(error, 'CommentBox attachment upload failed: storage request.')
    throw error
  }

  return uploadResponse
}

async function markUploadComplete(policy: UploadPolicyData, uploadResponse: Response) {
  // @ts-expect-error `asset_upload_url` is not defined on policy type
  const assetUploadUrl = typeof policy.asset_upload_url === 'string' ? policy.asset_upload_url : null
  const token =
    // @ts-expect-error `asset_upload_authenticity_token` is not defined on policy type
    typeof policy.asset_upload_authenticity_token == 'string' ? policy.asset_upload_authenticity_token : null

  if (assetUploadUrl && token) {
    const putForm = new FormData()
    // eslint-disable-next-line github/authenticity-token
    putForm.append('authenticity_token', token)

    let putResponse: Response
    try {
      putResponse = await fetch(assetUploadUrl, {
        method: 'PUT',
        body: putForm,
        credentials: getUploadCredentials(),
        headers: {
          Accept: 'application/json',
          ...getBaseFetchHeaders(),
        },
      })
    } catch (error) {
      reportAttachmentUploadError(error, 'CommentBox attachment upload failed: completion request.')
      throw error
    }
    return putResponse
  }
  return uploadResponse
}

export async function uploadFile(
  file: File,
  repositoryId?: string,
  projectId?: string,
  onProgress?: (uploadedBytes: number) => void,
) {
  const policyResponse = await getPolicy(file, repositoryId, projectId)
  if (!policyResponse.ok) throw new Error('Failed to obtain asset policy')

  const policy = (await policyResponse.json()) as UploadPolicyData
  const uploadResponse = await uploadFileToStorage(policy, file, onProgress)
  if (!uploadResponse.ok) throw new Error('Failed to upload file to storage')

  const putResponse = await markUploadComplete(policy, uploadResponse)
  if (!putResponse.ok) throw new Error('Failed to register file upload completion')

  const putResponseJson = (await putResponse.json()) as UploadResponseData
  const {href: url} = putResponseJson
  if (typeof url !== 'string') throw new Error('Received invalid file URL')

  // The completion response carries the asset's id on every storage backend; the policy
  // response is kept as a fallback since only its S3 shape declares the field.
  const assetId =
    typeof putResponseJson.id === 'number'
      ? putResponseJson.id
      : 'id' in policy.asset && typeof policy.asset.id === 'number'
        ? policy.asset.id
        : null

  onProgress?.(file.size)
  return {
    url,
    file,
    assetId,
  }
}

export function acceptedFileTypes(hasRepository: boolean) {
  /**
   * File types that are acceptable when there is a target repo to upload to.
   * Source: `ApplicationRecord::Domain::AssetObjects::RepositoryFile`
   */
  const repoAcceptedFileTypes: FileType[] = [
    ...REPOLESS_ACCEPTED_FILE_TYPES,
    ...(isFeatureEnabled('webp_support') ? REPO_WEBP_SUPPORT : []),
    ...REPO_BASELINE_FILE_TYPES,
    ...(isFeatureEnabled('issues_expanded_file_types') ? REPO_EXPANDED_FILE_TYPES : []),
  ]

  const repoLessAcceptedFileTypes: FileType[] = [
    ...REPOLESS_ACCEPTED_FILE_TYPES,
    ...(isFeatureEnabled('webp_support') ? REPO_WEBP_SUPPORT : []),
  ]
  return hasRepository ? repoAcceptedFileTypes : repoLessAcceptedFileTypes
}

export function useUploadFile(repositoryId?: string, projectId?: string) {
  return useCallback(
    async (file: File, onProgress?: (uploadedBytes: number) => void) => {
      return uploadFile(file, repositoryId, projectId, onProgress)
    },
    [repositoryId, projectId],
  )
}
